import { useEffect, useRef } from 'react';
import { API_ENDPOINTS } from 'config/ApiConfig';
import useAuthStore from 'Store/useAuthStore';
import useNotificationStoreV2 from 'Store/useNotificationStoreV2';
import { fetchPersistentNotifications } from 'Api/apiService';
import { isTokenValid } from 'Utils/tokenValidation';

// Any Unix-seconds value below this (year 2000) is considered bogus
const MIN_VALID_TS = 946684800;

/**
 * Resolve a Unix timestamp in SECONDS from the various shapes the backend sends:
 *
 *  Source A — send_notification():
 *    record_insert_time_stamp: "2026-04-09T04:14:54.406386+00:00"  ← ISO string, 6-decimal microseconds
 *
 *  Source B — send_user_notification():
 *    created_at: 1744172094   ← plain Unix integer (seconds)
 *
 * Returns null when no valid timestamp is found — callers must handle null gracefully.
 */
const resolveTimestamp = (data) => {
  const candidates = [
    data.record_insert_time_stamp,
    data.created_at,
    data.timestamp,
    data.ts,
  ];

  for (const raw of candidates) {
    if (raw == null || raw === '') continue;

    let seconds;
    const asNum = Number(raw);

    if (!isNaN(asNum)) {
      // Numeric string or number — could be seconds or milliseconds
      seconds = asNum > 1e12 ? Math.floor(asNum / 1000) : Math.floor(asNum);
    } else if (typeof raw === 'string') {
      // ISO date string from Python's datetime.isoformat()
      // Python produces 6 decimal places (microseconds); JS Date only handles 3 (ms)
      // Truncate extra decimals before parsing so Date.parse works in all browsers
      const safe = raw.replace(/(\.\d{3})\d+/, '$1');
      const ms = Date.parse(safe);
      if (isNaN(ms)) continue;
      seconds = Math.floor(ms / 1000);
    } else {
      continue;
    }

    if (seconds > MIN_VALID_TS) return seconds;
  }

  return null;
};

const normaliseSSEPayload = (data) => ({
  id: data.sb_notification_unique_id || data.id || String(Date.now()),
  message: data.message || '',
  message_type: data.message_type || 'info',
  entity_id: data.entity_id || null,
  context_url: data.context_url || null,
  created_at: resolveTimestamp(data),
});

const BASE_RECONNECT_DELAY = 2000;
const MAX_RECONNECT_DELAY = 30000;

/**
 * useGlobalSSE
 *
 * Manages the persistent notification stream for the bell icon.
 * Lifecycle: session (login → logout) — never terminates on its own.
 *
 * Uses a fetch-based SSE reader instead of EventSourcePolyfill to ensure all
 * network errors are caught and never surface as unhandled promise rejections.
 *
 * Task progress streams (job creation, workflow) are NOT handled here.
 * They are short-lived and owned by the component that starts the job (JobsLibraryV2).
 */
const useGlobalSSE = () => {
  const accessToken = useAuthStore((state) => state.accessToken);

  // On mount: load persisted notifications from DB (source of truth)
  useEffect(() => {
    if (!accessToken || !isTokenValid(accessToken, 'useGlobalSSE')) return;
    fetchPersistentNotifications()
      .then((messages) => {
        const normalised = (messages || []).map(normaliseSSEPayload);
        useNotificationStoreV2.getState().setNotifications(normalised);
      })
      .catch((err) => console.error('[GlobalSSE] fetch persistent error:', err));
  }, [accessToken]);

  // Notification stream — session lifetime, reconnects with exponential backoff
  useEffect(() => {
    if (!accessToken || !isTokenValid(accessToken, 'useGlobalSSE')) return;

    let cancelled = false;
    let abortController = null;
    let reconnectTimer = null;
    let reconnectDelay = BASE_RECONNECT_DELAY;

    const connect = async () => {
      if (cancelled) return;

      abortController = new AbortController();

      try {
        const response = await fetch(API_ENDPOINTS.NOTIFICATION_STREAM, {
          signal: abortController.signal,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
        });

        if (!response.ok) {
          console.error(`[GlobalSSE] stream responded ${response.status} — will retry`);
          scheduleReconnect();
          return;
        }

        // Successful connection — reset backoff
        reconnectDelay = BASE_RECONNECT_DELAY;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done || cancelled) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete trailing line

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (!data.message) continue;
              useNotificationStoreV2.getState().addNotification(normaliseSSEPayload(data));
            } catch (_) {
              // ignore unparseable SSE lines
            }
          }
        }

        if (!cancelled) scheduleReconnect();
      } catch (err) {
        if (err.name === 'AbortError' || cancelled) return;
        console.error('[GlobalSSE] connection error:', err.message, '— will retry');
        scheduleReconnect();
      }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      reconnectTimer = setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
        connect();
      }, reconnectDelay);
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      abortController?.abort();
    };

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      abortController?.abort();
    };
  }, [accessToken]);
};

export default useGlobalSSE;
